import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { captureSlowOperation } from "../../sentry";

const CONSOLE_API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8020";

const dynamicBaseQuery = async (args, api, extraOptions) => {
  const start = Date.now();
  const useProd = args.params?.use_prod === true;

  if (args.params) {
    const { use_prod, ...cleanParams } = args.params;
    args.params = Object.keys(cleanParams).length > 0 ? cleanParams : undefined;
  }

  const rawBaseQuery = fetchBaseQuery({
    baseUrl: `${CONSOLE_API_BASE}/platform-proxy`,
    credentials: "include",
    prepareHeaders: (headers, { getState }) => {
      headers.set("Content-Type", "application/json");

      const token = getState().auth.token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
  });

  if (!args.params) {
    args.params = {};
  }
  args.params.use_prod = useProd;

  const result = await rawBaseQuery(args, api, extraOptions);
  const durationMs = Date.now() - start;
  const thresholdMs = Number(process.env.REACT_APP_SENTRY_SLOW_API_THRESHOLD_MS || "3000");
  if (durationMs >= thresholdMs) {
    captureSlowOperation("console_frontend_api_call", durationMs, thresholdMs, {
      api: "platform-proxy",
      endpoint: api.endpoint,
      url: args.url || "unknown",
      method: args.method || "GET",
      status: result?.error?.status || "ok",
    });
  }
  return result;
};

export const platformApiSlice = createApi({
  reducerPath: "platformApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "Websites",
    "Articles",
    "Scans",
    "Issues",
    "ActivityRuns",
    "AutomatedScanTemplates",
    "QualityMetrics",
    "ScanTemplatesV2",
    "PromptsV2",
    "PromptVersionsV2",
  ],
  endpoints: (builder) => ({
    getWithEnvironment: builder.query({
      query: ({ url, useProd = false }) => {
        return {
          url,
          params: { use_prod: useProd },
        };
      },
    }),

    // Optimized endpoint for AcquisitionTools page
    getWebsitesAcquisitionStatus: builder.query({
      query: ({ websiteIds, useProd = false }) => {
        return {
          url: "/api/v1/websites/acquisition-tools/bulk-status",
          method: "POST",
          body: websiteIds,
          params: { use_prod: useProd },
        };
      },
      providesTags: ["Websites", "Scans", "Issues"],
    }),

    // Agent Analytics endpoint - get scans for multiple websites
    getWebsitesAnalyticsScans: builder.query({
      query: ({ websiteIds, useProd = false }) => {
        return {
          url: "/api/v1/websites/analytics/scans",
          method: "POST",
          body: websiteIds,
          params: { use_prod: useProd },
        };
      },
      providesTags: ["Websites", "Scans"],
    }),

    // Get scope URLs for a scan
    getScanScopeUrls: builder.query({
      query: ({ scanId, useProd = false }) => {
        return {
          url: `/api/v1/scans/${scanId}/scope-urls`,
          params: { use_prod: useProd },
        };
      },
      providesTags: (result, error, { scanId }) => [{ type: "Scans", id: scanId }],
    }),

    getAcquisitionBulkCounts: builder.query({
      query: ({ websiteIds, useProd = false }) => ({
        url: "/api/v1/websites/acquisition-bulk-counts",
        method: "POST",
        body: { website_ids: websiteIds },
        params: { use_prod: useProd },
      }),
    }),

    // Issue Analytics — per-(website, scan_template) status counts
    getWebsitesAnalyticsIssues: builder.query({
      // dateField: "created_at" | "updated_at" (window clock); bucket: "week" for one
      // row per ISO week (bucket_start) — the page sums weekly rows for its totals.
      query: ({ websiteIds, startDate = null, endDate = null, dateField = null, bucket = null, useProd = true }) => ({
        url: "/api/v1/websites/analytics/issues",
        method: "POST",
        body: {
          website_ids: websiteIds,
          start_date: startDate,
          end_date: endDate,
          date_field: dateField,
          bucket,
        },
        params: { use_prod: useProd },
      }),
      providesTags: ["Websites", "Issues"],
    }),

    // Issue Analytics — discarded-issue firehose (with discard_reason)
    getWebsitesAnalyticsIssueDiscards: builder.query({
      query: ({ websiteIds, startDate = null, endDate = null, dateField = null, useProd = true }) => ({
        url: "/api/v1/websites/analytics/issue-discards",
        method: "POST",
        body: {
          website_ids: websiteIds,
          start_date: startDate,
          end_date: endDate,
          date_field: dateField,
        },
        params: { use_prod: useProd },
      }),
      providesTags: ["Websites", "Issues"],
    }),
  }),
});

export const {
  useGetWithEnvironmentQuery,
  useGetWebsitesAcquisitionStatusQuery,
  useGetWebsitesAnalyticsScansQuery,
  useLazyGetScanScopeUrlsQuery,
  useGetAcquisitionBulkCountsQuery,
  useGetWebsitesAnalyticsIssuesQuery,
  useGetWebsitesAnalyticsIssueDiscardsQuery,
} = platformApiSlice;
